import { useQueryClient } from '@tanstack/react-query';
import {
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  resetPassword,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updateMFAPreference,
  updateUserAttribute,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage, sessionStorage } from 'aws-amplify/utils';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clearCertificatesAnnouncementSessionDismissal } from '../../components/dialog-manager/dialogs/certificates-announcement-dialog/session-dismissal.utils';
import { Locale } from '../../i18n';
import generateAuthenticatorResetEmailBody from '../../pages/login/components/totp-mfa-form/generateEmailBody';
import { SUPPORT_EMAIL } from '../../shared/constants/emails.constant';
import { BASF_COMPANY_ID } from '../../shared/constants/id.constant';
import {
  CognitoErrorCode,
  CognitoErrorMessage,
  CognitoMfaErrorMessage,
  CognitoMfaErrorName,
} from '../../shared/enums/cognito-error-code';
import { LoginErrorCode } from '../../shared/enums/login-error-code';
import { clearOptimisticCertificatePermissions } from '../../shared/helpers/optimistic-certificate-permissions';
import { User, UserRole } from '../../shared/types/User';
import { IUserDetails, useGetUser } from '../api/users';

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  isPhoneVerified: boolean;
  user: User | null;
  signIn: (username: string, password: string, staySignedIn: boolean) => Promise<Result>;
  signOut: () => Promise<{ success: boolean; message: string }>;
  completeNewPassword: (
    username: string,
    oldPassowrd: string,
    newPassword: string,
  ) => Promise<Result>;
  sendVerificationCode: (
    username: string,
  ) => Promise<{ success: boolean; deliveryMedium?: string; message: string }>;
  confirmResetPassword: (
    username: string,
    confirmCode: string,
    newPassword: string,
  ) => Promise<{ success: boolean; message: string }>;
  confirmMfaCode: (otpCode: string) => Promise<Result>;
  initiateTotpSetup: (
    isPostAuth: boolean,
  ) => Promise<{ success: boolean; sharedSecret?: string | null }>;
  confirmTotpSetup: (otpCode: string, isPostAuth: boolean) => Promise<Result>;
  confirmTotpMfaCode: (otpCode: string) => Promise<Result>;
  sendPhoneVerificationCode: (
    phoneNumber: string,
  ) => Promise<{ success: boolean; message: string }>;
  verifyCode: (code: string) => Promise<{ success: boolean; message: string }>;
  resendVerificationCode: () => Promise<{ success: boolean; message: string }>;
  getAuthenticatorResetMessage: (email: string) => string;
  isCompanyAdmin: boolean;
  isBasfSuperAdmin: boolean;
  isBasfNonAdmin: boolean;
  isBasfAdmin: boolean;
  isBasfUser: boolean;
}

interface Result {
  success: boolean;
  message: string;
  errorCode?: LoginErrorCode;
}

type Props = {
  children?: React.ReactNode;
};

const authContext = createContext({} as UseAuth);

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const auth = useAuthProvider();

  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useAuthProvider = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { refetch: fetchMyData, isStale } = useGetUser();

  const queryClient = useQueryClient();

  const isInitialMountRef = useRef(true);

  useEffect(() => {
    async function fetchUser() {
      const isInitialMount = isInitialMountRef.current;
      isInitialMountRef.current = false;

      try {
        // Get session
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        if (!idToken) {
          // If there is no session, then logout
          await amplifySignOut();
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);

          return;
        }

        const { username } = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const hasTOTP = await hasTOTPEnabled();
        if (!hasTOTP) {
          // Partial-auth state: Cognito session valid but TOTP not set up yet.
          // On initial mount: treat as abandoned setup and clear tokens so the user
          // must re-authenticate. On subsequent runs (triggered by isStale changes
          // during an in-flight signIn), do NOT sign out — that would revoke the
          // session a fresh signIn just created and break the TOTP setup flow.
          if (isInitialMount) {
            await amplifySignOut();
            setUser(null);
            setIsAuthenticated(false);
            setIsSuperAdmin(false);
            setIsLoading(false);

            return;
          }
          setIsAuthenticated(false);
          setIsLoading(false);

          return;
        }
        setIsAuthenticated(true);

        const userGroups = idToken?.payload['cognito:groups'] as string[];
        setIsSuperAdmin(userGroups?.includes('super-admin') ?? false);

        // If authentication check is successful, then fetch user data from API
        const { data: userData } = await fetchMyData({ throwOnError: true });

        setUser(createUserObject(userData, username, userAttributes));

        setIsPhoneVerified(Boolean(userAttributes?.phone_number_verified === 'true'));
        setIsLoading(false);
      } catch (error) {
        // If there is an error during the page refresh, then logout
        // Logout is needed, to clear the storages and reset the session
        await amplifySignOut();
        setUser(null);
        setIsAuthenticated(false);
        setIsSuperAdmin(false);
        setIsLoading(false);
      }
    }

    fetchUser();
  }, [isStale]);

  const signIn = async (username: string, password: string, staySignedIn: boolean) => {
    try {
      // Configure storage for session
      if (staySignedIn) {
        // If staySignedIn is true, then use local storage, which is referred as default storage
        cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
      } else {
        // If staySignedIn is false, then use session storage
        cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
      }

      const result = await amplifySignIn({ username, password });

      if (result.isSignedIn) {
        // If login is successful, then go further and fetch user attributes/data
        const userAttributes = await fetchUserAttributes();
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        const userGroups = idToken?.payload['cognito:groups'] as string[];
        setIsSuperAdmin(userGroups?.includes('super-admin') ?? false);

        // If login is successful, then fetch user data from API
        const { data: userData } = await fetchMyData({ throwOnError: true });

        const hasTOTP = await hasTOTPEnabled();
        setUser(createUserObject(userData, username, userAttributes));
        setIsPhoneVerified(Boolean(userAttributes?.phone_number_verified === 'true'));
        if (!hasTOTP) {
          setIsAuthenticated(false);

          // Signal to frontend that TOTP setup is required
          return {
            success: false,
            message: 'TOTP_SETUP_REQUIRED',
          };
        } else {
          setIsAuthenticated(true);
        }

        return { success: true, message: result.nextStep.signInStep ?? '' };
        // } else if (result.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        //   // When this message is received, configure TOTP as the default MFA
        //   // and proceed with the subsequent step.
        //   const mfaSelectionResult = await confirmSignIn({ challengeResponse: 'TOTP' });
        //   return { success: false, message: mfaSelectionResult.nextStep.signInStep ?? '' };
      } else {
        return { success: false, message: result.nextStep.signInStep ?? '' };
      }
    } catch (error) {
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      setIsLoading(false);

      // Default error message
      let message = t('authPage.loginForm.errorMessage');
      let errorCode = LoginErrorCode.InvalidCredentials;

      // Check error cause, if user is disabled, then show user disabled message
      if ((error as Error).message === CognitoErrorMessage.UserDisabledException) {
        message = t('authPage.loginForm.userDisabledMessage');
        errorCode = LoginErrorCode.UserDisabled;
      }

      // Check if user's terms of use consent is outdated
      if ((error as Error).message === 'CONSENT_TERMS_OUTDATED') {
        errorCode = LoginErrorCode.OutdatedTermsConsent;
      }

      return {
        success: false,
        message: message,
        errorCode: errorCode,
      };
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();

      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      queryClient.invalidateQueries(['users', 'me']);

      // Clear React Query cache
      queryClient.clear();

      clearCertificatesAnnouncementSessionDismissal();

      // Backed by sessionStorage, so queryClient.clear() above does not reach it.
      clearOptimisticCertificatePermissions(queryClient);

      return { success: true, message: '' };
    } catch (error) {
      return {
        success: false,
        message: t('authPage.loginForm.logoutFail'),
      };
    }
  };

  const hasTOTPEnabled = async () => {
    const { enabled } = await fetchMFAPreference();

    return enabled?.includes('TOTP') ?? false;
  };

  const completeNewPassword = async (
    username: string,
    oldPassowrd: string,
    newPassword: string,
  ) => {
    try {
      const loginResult = await amplifySignIn({ username, password: oldPassowrd });

      if (loginResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        const confirmSignInResult = await confirmSignIn({
          challengeResponse: newPassword,
        });

        if (confirmSignInResult.isSignedIn) {
          const userAttributes = await fetchUserAttributes();
          const { idToken } = (await fetchAuthSession()).tokens ?? {};
          const userGroups = idToken?.payload['cognito:groups'] as string[];
          setIsSuperAdmin(userGroups?.includes('super-admin') ?? false);

          const { enabled } = await fetchMFAPreference();
          const hasTOTP = Boolean(
            enabled?.find((method) => {
              return method === 'TOTP';
            }),
          );

          if (!hasTOTP) {
            setIsAuthenticated(false);

            return {
              success: false,
              message: 'TOTP_SETUP_REQUIRED', // custom flag to initiate TOTP setup flow in frontend
            };
          } else {
            setIsAuthenticated(true);
          }

          // If activation is successful, then fetch user data from API
          const { data: userData } = await fetchMyData({ throwOnError: true });

          setUser(createUserObject(userData, username, userAttributes));

          setIsPhoneVerified(Boolean(userAttributes?.phone_number_verified === 'true'));

          return { success: true, message: '' };
        } else {
          return { success: false, message: confirmSignInResult.nextStep.signInStep ?? '' };
        }
      }

      return { success: false, message: loginResult.nextStep.signInStep ?? '' };
    } catch (error) {
      const mappedMessage =
        CognitoErrorCode[(error as Error).name as keyof typeof CognitoErrorCode];

      const errorMessage = (error as Error).message;

      return {
        success: false,
        message: mappedMessage ?? errorMessage,
      };
    }
  };

  // This function is used to generate lost authenticator message with mailto link
  const getAuthenticatorResetMessage = (email: string) => {
    const emailSubject = encodeURIComponent(t('emails.authenticatorReset.subject'));
    const emailBody = encodeURIComponent(
      generateAuthenticatorResetEmailBody(email, language as Locale),
    );

    return t('authPage.mfaTOTPForm.lostAuthenticator.infoMessage', {
      supportEmail: SUPPORT_EMAIL,
      emailSubject,
      emailBody,
    });
  };

  const sendVerificationCode = async (username: string) => {
    try {
      const result = await resetPassword({ username });

      return {
        success: true,
        deliveryMedium: result.nextStep.codeDeliveryDetails.deliveryMedium,
        message: t('authPage.loginForm.SendCodeSuccessfully'),
      };
    } catch (error) {
      // Default error message
      let message = t('authPage.forgotPassword.errorMessage');

      if ((error as Error).message === CognitoErrorMessage.UserDisabledException) {
        message = t('authPage.forgotPassword.userDisabledMessage');
      } else if ((error as Error).message === CognitoErrorMessage.UserNotFoundException) {
        message = t('authPage.forgotPassword.userNotFound');
      }

      return {
        success: false,
        message,
      };
    }
  };

  const confirmResetPassword = async (
    username: string,
    confirmationCode: string,
    newPassword: string,
  ) => {
    try {
      await amplifyConfirmResetPassword({ username, confirmationCode, newPassword });

      return {
        success: true,
        message: t('authPage.loginForm.passwordResetSuccessfully'),
      };
    } catch (error) {
      const mappedMessage =
        CognitoErrorCode[(error as Error).name as keyof typeof CognitoErrorCode];
      const errorMessage = (error as Error).message;

      return {
        success: false,
        message: mappedMessage || errorMessage,
      };
    }
  };

  const initiateTotpSetup = async (isPostAuth = false) => {
    try {
      if (isPostAuth) {
        const response = await setUpTOTP();

        return {
          success: true,
          sharedSecret: response.sharedSecret,
        };
      } else {
        const response = await confirmSignIn({ challengeResponse: 'TOTP' });
        if (response.nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
          return { success: true, sharedSecret: response.nextStep.totpSetupDetails.sharedSecret };
        }
      }

      return { success: false, sharedSecret: null };
    } catch (error) {
      return { success: false, sharedSecret: null };
    }
  };

  // Sign in using two factor authentication code
  const confirmMfaCode = async (otpCode: string) => {
    try {
      // Send the OTP code to AWS Cognito
      const mfaSignInResult = await confirmSignIn({
        challengeResponse: otpCode,
      });

      if (mfaSignInResult.isSignedIn) {
        const { username } = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        const userGroups = idToken?.payload['cognito:groups'] as string[];
        setIsSuperAdmin(userGroups?.includes('super-admin') ?? false);

        const hasTOTP = await hasTOTPEnabled();
        if (!hasTOTP) {
          setIsAuthenticated(false);

          return {
            success: true,
            message: 'TOTP_SETUP_REQUIRED', // custom flag to initiate TOTP setup flow in frontend
          };
        } else {
          setIsAuthenticated(true);
        }
        // Fetch user data from API
        const { data: userData } = await fetchMyData({ throwOnError: true });

        setUser(createUserObject(userData, username, userAttributes));
        setIsPhoneVerified(Boolean(userAttributes?.phone_number_verified === 'true'));

        return { success: true, message: '' };
      }

      return { success: false, message: '' };
    } catch (error) {
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      setIsLoading(false);

      const errorObject = error as Error;

      let message = '';
      let errorCode = undefined;

      if (errorObject.message === CognitoMfaErrorMessage.SessionExpired) {
        message = t('authPage.mfaForm.sessionExpiredErrorMessage');
      } else if (errorObject.name === CognitoMfaErrorName.CodeMismatchException) {
        message = t('authPage.mfaForm.invalidCodeErrorMessage');
      } else if (errorObject.message === CognitoMfaErrorMessage.TooManyInvalidAttempts) {
        message = t('authPage.mfaForm.tooManyInvalidAttemptsErrorMessage');
      } else if (
        errorObject.message.replace(/\t|\n/g, '') ===
        CognitoMfaErrorMessage.AttemptedWithoutSigningIn
      ) {
        message = t('authPage.mfaForm.attemptWithoutSigningInErrorMessage');
      } // Check if user's terms of use consent is outdated
      else if ((error as Error).message === 'CONSENT_TERMS_OUTDATED') {
        (message = ''), (errorCode = LoginErrorCode.OutdatedTermsConsent);
      } else {
        message = (error as Error).message;
      }

      return { success: false, message: message, errorCode };
    }
  };

  const configureUserData = async () => {
    const { username } = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();
    const { idToken } = (await fetchAuthSession()).tokens ?? {};
    const userGroups = idToken?.payload['cognito:groups'] as string[];
    setIsSuperAdmin(userGroups?.includes('super-admin') ?? false);

    const { data: userData } = await fetchMyData({ throwOnError: true });

    setUser(createUserObject(userData, username, userAttributes));
    setIsPhoneVerified(Boolean(userAttributes?.phone_number_verified === 'true'));
    setIsAuthenticated(true);
  };

  const confirmTotpSetup = async (otpCode: string, isPostAuth: boolean) => {
    try {
      if (isPostAuth) {
        await verifyTOTPSetup({ code: otpCode });
        await updateMFAPreference({ totp: 'ENABLED', email: 'DISABLED' });
        await configureUserData();

        return { success: true, message: '' };
      } else {
        // Mid challenge — same as confirmMfaCode
        const result = await confirmSignIn({ challengeResponse: otpCode });

        if (result.isSignedIn) {
          await updateMFAPreference({ totp: 'ENABLED', email: 'DISABLED' });
          await configureUserData();

          return { success: true, message: '' };
        }
      }

      return { success: false, message: '' };
    } catch (error) {
      const errorObject = error as Error;

      if (
        errorObject.name === CognitoMfaErrorName.CodeMismatchException ||
        errorObject.name === CognitoMfaErrorName.EnableSoftwareTokenMFAException
      ) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.codeMismatch') };
      }

      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      setIsLoading(false);

      if (errorObject.message === CognitoMfaErrorMessage.SessionExpired) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.expiredCode') };
      }

      if (errorObject.message === CognitoMfaErrorMessage.TooManyInvalidAttempts) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.invalidAttempts') };
      }

      return { success: false, message: errorObject.message };
    }
  };

  // Sign in using two factor authentication code for TOTP
  const confirmTotpMfaCode = async (otpCode: string) => {
    try {
      // Send the OTP code to AWS Cognito
      const mfaSignInResult = await confirmSignIn({
        challengeResponse: otpCode,
      });

      if (mfaSignInResult.isSignedIn) {
        await configureUserData();

        return { success: true, message: '' };
      }

      return { success: false, message: '' };
    } catch (error) {
      const errorObject = error as Error;

      if (
        errorObject.name === CognitoMfaErrorName.CodeMismatchException ||
        errorObject.name === CognitoMfaErrorName.EnableSoftwareTokenMFAException
      ) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.codeMismatch') };
      }

      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsSuperAdmin(false);
      setIsLoading(false);

      if (errorObject.message === CognitoMfaErrorMessage.SessionExpired) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.expiredCode') };
      }

      if (errorObject.message === CognitoMfaErrorMessage.TooManyInvalidAttempts) {
        return { success: false, message: t('authPage.mfaTOTPForm.errorMessages.invalidAttempts') };
      }

      return { success: false, message: errorObject.message };
    }
  };

  const sendPhoneVerificationCode = async (phoneNumber: string) => {
    if (isPhoneVerified) {
      return {
        success: false,
        message: t('phoneVerificationDialog.phoneAlreadyVerified'),
      };
    }

    try {
      const { isUpdated } = await updateUserAttribute({
        userAttribute: {
          attributeKey: 'phone_number',
          value: phoneNumber,
        },
      });

      if (!isUpdated) {
        throw new Error();
      }

      return resendVerificationCode();
    } catch (error) {
      return {
        success: false,
        message: t('phoneVerificationDialog.codeSentFailed'),
      };
    }
  };

  const resendVerificationCode = async () => {
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });

      return {
        success: true,
        message: t('phoneVerificationDialog.codeSentSuccessfully'),
      };
    } catch (err) {
      return {
        success: false,
        message: t('phoneVerificationDialog.codeSentFailed'),
      };
    }
  };

  const verifyCode = async (code: string) => {
    try {
      await confirmUserAttribute({
        confirmationCode: code,
        userAttributeKey: 'phone_number',
      });

      return {
        success: true,
        message: t('phoneVerificationDialog.codeVerifiedSuccessfully'),
      };
    } catch (error) {
      const errorCode = (error as { name: string }).name;

      return {
        success: false,
        message:
          errorCode === 'CodeMismatchException'
            ? t('phoneVerificationDialog.codeMismatch')
            : t('phoneVerificationDialog.codeVerificationFailed'),
      };
    }
  };

  const isCompanyAdmin = user?.role === 'company-admin';
  const isBasfUser = user?.company?.company_id === BASF_COMPANY_ID;
  const isBasfSuperAdmin = isBasfUser && isSuperAdmin;
  const isBasfNonAdmin = isBasfUser && !isSuperAdmin;
  const isBasfAdmin = isBasfUser && (isSuperAdmin || user?.role === 'company-admin');

  return {
    isLoading,
    isPhoneVerified,
    isAuthenticated,
    user,
    signIn,
    signOut,
    completeNewPassword,
    sendVerificationCode,
    confirmResetPassword,
    initiateTotpSetup,
    confirmMfaCode,
    confirmTotpMfaCode,
    confirmTotpSetup,
    sendPhoneVerificationCode,
    resendVerificationCode,
    verifyCode,
    getAuthenticatorResetMessage,
    isCompanyAdmin,
    isBasfSuperAdmin,
    isBasfNonAdmin,
    isBasfAdmin,
    isBasfUser,
  };
};

// Helper function to create user object
const createUserObject = (
  userData?: IUserDetails,
  username?: string,
  userAttributes?: FetchUserAttributesOutput,
): User => {
  const role = userAttributes?.['custom:role'] as UserRole;

  return {
    id: userData?.id,
    username: username || '',
    email: userData?.email ?? '',
    givenName: userData?.firstName ?? '',
    familyName: userData?.lastName ?? '',
    company: {
      id: userData?.company?.id ?? 0,
      company_id: userData?.company?.companyId ?? '', // eslint-disable-line camelcase
      company_name: userData?.company?.companyName ?? '', // eslint-disable-line camelcase
      edcConfigurationStatus: userData?.company?.edcConfigurationStatus,
      license: userData?.company?.license || 'basic',
      mfa_enabled: userData?.company?.isMfaEnabled || false, // eslint-disable-line camelcase
      isCertificateManagementEnabled: userData?.company?.isCertificateManagementEnabled,
      isCatenaXPcfExchangeEnabled: userData?.company?.isCatenaXPcfExchangeEnabled,
    },
    role,
    consentTermsVersion: userData?.consentTermsVersion ?? '',
    isCompanyIdentifierExist: userData?.isCompanyIdentifierExist,
    certificatesAnnouncementDismissedAt: userData?.certificatesAnnouncementDismissedAt,
  };
};
